import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import raf from 'raf';
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import { loadDailymotion, loadYouTube, loadVimeo } from '@folklore/services';
import createDebug from 'debug';
import EventsManager from '@folklore/events';
import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';

function useCounter(desiredValue, _ref) {
  var _ref$maxDuration = _ref.maxDuration,
      maxDuration = _ref$maxDuration === void 0 ? 2000 : _ref$maxDuration,
      _ref$speed = _ref.speed,
      speed = _ref$speed === void 0 ? 1 / 10 : _ref$speed;

  var _useState = useState(desiredValue),
      _useState2 = _slicedToArray(_useState, 2),
      currentValue = _useState2[0],
      setCurrentValue = _useState2[1];

  useEffect(function () {
    var animationFrame = null;
    var startTime = null;
    var duration = 0;
    var canceled = false;
    var startValue = currentValue;
    var delta = desiredValue - startValue;

    function loop() {
      if (canceled) {
        return;
      }

      var currentTime = Date.now();
      var elapsedTime = currentTime - startTime;
      var progress = Math.min(elapsedTime / duration, 1);
      var newValue = Math.round(startValue + progress * delta);
      setCurrentValue(newValue);

      if (newValue !== desiredValue) {
        animationFrame = raf(loop);
      }
    }

    if (delta !== 0) {
      duration = Math.min(maxDuration, Math.abs(delta) * speed * 1000);
      startTime = Date.now();
      animationFrame = raf(loop);
    }

    return function () {
      canceled = true;

      if (animationFrame !== null) {
        raf.cancel(animationFrame);
      }
    };
  }, [desiredValue]);
  return currentValue;
}

function usePlayerCurrentTime(player) {
  var _ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
      _ref$id = _ref.id,
      id = _ref$id === void 0 ? null : _ref$id,
      _ref$disabled = _ref.disabled,
      disabled = _ref$disabled === void 0 ? false : _ref$disabled,
      _ref$updateInterval = _ref.updateInterval,
      updateInterval = _ref$updateInterval === void 0 ? 1000 : _ref$updateInterval,
      _ref$onUpdate = _ref.onUpdate,
      customOnUpdate = _ref$onUpdate === void 0 ? null : _ref$onUpdate,
      _ref$getCurrentTime = _ref.getCurrentTime,
      getCurrentTime = _ref$getCurrentTime === void 0 ? function (p) {
    return p.currentTime;
  } : _ref$getCurrentTime;

  var _useState = useState(0),
      _useState2 = _slicedToArray(_useState, 2),
      currentTime = _useState2[0],
      setCurrentTime = _useState2[1];

  var realCurrentTime = useRef(currentTime);
  var lastIdRef = useRef(id);
  var idChanged = lastIdRef.current !== id;

  if (idChanged) {
    realCurrentTime.current = 0;
    lastIdRef.current = id;
  } // Check time update


  useEffect(function () {
    if (disabled || player === null) {
      return function () {};
    }

    var canceled = false;

    var updateTime = function updateTime(time) {
      if (canceled) {
        return;
      }

      realCurrentTime.current = time;
      setCurrentTime(time);

      if (customOnUpdate !== null) {
        customOnUpdate(time);
      }
    };

    var interval = setInterval(function () {
      var time = getCurrentTime(player);

      if (typeof time.then !== 'undefined') {
        time.then(updateTime);
      } else {
        updateTime(time);
      }
    }, updateInterval);
    return function () {
      canceled = true;
      clearInterval(interval);
    };
  }, [id, player, setCurrentTime, disabled, updateInterval, getCurrentTime]);
  return realCurrentTime.current;
}

function ownKeys$4(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$4(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys$4(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys$4(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
var noPlayerError$1 = new Error('No player');
var debug$3 = createDebug('folklore:video:dailymotion');

var useDailymotionPlayer = function useDailymotionPlayer() {
  var id = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
  var params = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var _params$width = params.width,
      width = _params$width === void 0 ? 0 : _params$width,
      _params$height = params.height,
      height = _params$height === void 0 ? 0 : _params$height,
      _params$duration = params.duration,
      duration = _params$duration === void 0 ? 0 : _params$duration,
      _params$autoplay = params.autoplay,
      autoplay = _params$autoplay === void 0 ? false : _params$autoplay,
      _params$muted = params.muted,
      initialMuted = _params$muted === void 0 ? false : _params$muted,
      _params$start = params.start,
      start = _params$start === void 0 ? 0 : _params$start,
      _params$controls = params.controls,
      controls = _params$controls === void 0 ? true : _params$controls,
      _params$queueAutoplay = params.queueAutoplayNext,
      queueAutoplayNext = _params$queueAutoplay === void 0 ? false : _params$queueAutoplay,
      _params$queueEnable = params.queueEnable,
      queueEnable = _params$queueEnable === void 0 ? false : _params$queueEnable,
      _params$sharingEnable = params.sharingEnable,
      sharingEnable = _params$sharingEnable === void 0 ? false : _params$sharingEnable,
      _params$uiLogo = params.uiLogo,
      uiLogo = _params$uiLogo === void 0 ? false : _params$uiLogo,
      _params$uiStartScreen = params.uiStartScreenInfo,
      uiStartScreenInfo = _params$uiStartScreen === void 0 ? true : _params$uiStartScreen,
      _params$timeUpdateInt = params.timeUpdateInterval,
      timeUpdateInterval = _params$timeUpdateInt === void 0 ? 1000 : _params$timeUpdateInt,
      _params$onTimeUpdate = params.onTimeUpdate,
      customOnTimeUpdate = _params$onTimeUpdate === void 0 ? null : _params$onTimeUpdate,
      _params$getVideoId = params.getVideoId,
      getVideoId = _params$getVideoId === void 0 ? function (url) {
    if (url === null || url.match(/^https?:/) === null) {
      return url;
    }

    var match = url.match(/\/video\/([^/?]+)/);
    return match !== null ? match[1] : null;
  } : _params$getVideoId;

  var _useState = useState(typeof window.DM !== 'undefined'),
      _useState2 = _slicedToArray(_useState, 2),
      apiLoaded = _useState2[0],
      setApiLoaded = _useState2[1];

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      playerReady = _useState4[0],
      setPlayerReady = _useState4[1];

  var _useState5 = useState(false),
      _useState6 = _slicedToArray(_useState5, 2),
      loaded = _useState6[0],
      setLoaded = _useState6[1];

  var apiRef = useRef(typeof window.DM !== 'undefined' ? window.DM : null);
  var ready = apiLoaded && playerReady;
  var videoId = useMemo(function () {
    return getVideoId(id);
  }, [id]);
  var elementRef = useRef(null);
  var playerRef = useRef(null);
  var playerElementRef = useRef(elementRef.current);
  var elementHasChanged = elementRef.current !== playerElementRef.current;

  var _useState7 = useState(initialMuted),
      _useState8 = _slicedToArray(_useState7, 2),
      muted = _useState8[0],
      setMuted = _useState8[1];

  var _useState9 = useState(initialMuted ? 0 : 1),
      _useState10 = _slicedToArray(_useState9, 2),
      volume = _useState10[0],
      setVolumeState = _useState10[1];

  var _useState11 = useState({
    playing: false,
    paused: false,
    ended: false,
    buffering: false,
    adPlaying: false
  }),
      _useState12 = _slicedToArray(_useState11, 2),
      playState = _useState12[0],
      setPlayState = _useState12[1];

  var _useState13 = useState({
    width: width,
    height: height,
    duration: duration
  }),
      _useState14 = _slicedToArray(_useState13, 2),
      metadata = _useState14[0],
      setMetadata = _useState14[1]; // Load SDK


  useEffect(function () {
    var canceled = false;

    if (!apiLoaded && videoId !== null) {
      debug$3('Load API');
      loadDailymotion().then(function (api) {
        if (!canceled) {
          apiRef.current = api;
          setApiLoaded(true);
          debug$3('API Loaded');
        }
      });
    }

    return function () {
      canceled = true;
    };
  }, [videoId, apiLoaded, setApiLoaded]); // Create or update player

  useEffect(function () {
    var _apiRef$current = apiRef.current,
        DM = _apiRef$current === void 0 ? null : _apiRef$current;
    var _playerRef$current = playerRef.current,
        currentPlayer = _playerRef$current === void 0 ? null : _playerRef$current;
    var _elementRef$current = elementRef.current,
        element = _elementRef$current === void 0 ? null : _elementRef$current;

    if (!apiLoaded || videoId === null || element === null) {
      return;
    }

    var playerParams = {
      'autoplay-d': autoplay,
      // muted,
      start: start,
      controls: controls,
      'queue-autoplay-next': queueAutoplayNext,
      'queue-enable': queueEnable,
      'sharing-enable': sharingEnable,
      'ui-logo': uiLogo,
      'ui-start-screen-info': uiStartScreenInfo
    };
    var player = currentPlayer;

    if (player !== null) {
      player.load(videoId, {
        params: playerParams
      });
      debug$3('Load video [ID: %s]', videoId);
    } else {
      player = DM.player(element, {
        video: videoId,
        width: width,
        height: height,
        params: playerParams
      });
      debug$3('Create player [ID: %s]', videoId);
    }

    if (!playerReady) {
      setPlayerReady(true);
    }

    playerRef.current = player;
    playerElementRef.current = element;
  }, [apiLoaded, elementHasChanged, videoId, width, height, autoplay, muted, start, controls, queueAutoplayNext, queueEnable, sharingEnable, uiLogo, uiStartScreenInfo]);
  useEffect(function () {
    var _playerRef$current2 = playerRef.current,
        player = _playerRef$current2 === void 0 ? null : _playerRef$current2;

    if (player === null) {
      return function () {};
    }

    var currentPlayState = playState;
    var currentMetadata = metadata;

    function onPlaybackReady() {
      setLoaded(true);
      debug$3('onPlaybackReady [ID: %s]', videoId);
    }

    function onLoadedMetadata() {
      currentMetadata = _objectSpread$4(_objectSpread$4({}, currentMetadata), {}, {
        duration: player.duration
      });
      setMetadata(currentMetadata);
      debug$3('onLoadedMetadata [ID: %s]', videoId);
    }

    function onDurationChange() {
      currentMetadata = _objectSpread$4(_objectSpread$4({}, currentMetadata), {}, {
        duration: player.duration
      });
      setMetadata(currentMetadata);
      debug$3('onDurationChange [ID: %s]', videoId);
    }

    function onVolumeChange() {
      setMuted(player.muted);
      setVolumeState(player.volume);
      debug$3('onVolumeChange [ID: %s]', videoId);
    }

    function onPlay() {
      currentPlayState = _objectSpread$4(_objectSpread$4({}, currentPlayState), {}, {
        playing: true,
        paused: false,
        ended: false
      });
      setPlayState(currentPlayState);
      debug$3('onPlay [ID: %s]', videoId);
    }

    function onPause() {
      currentPlayState = _objectSpread$4(_objectSpread$4({}, currentPlayState), {}, {
        playing: false,
        paused: true,
        ended: false
      });
      setPlayState(currentPlayState);
      debug$3('onPause [ID: %s]', videoId);
    }

    function onEnd() {
      currentPlayState = _objectSpread$4(_objectSpread$4({}, currentPlayState), {}, {
        playing: false,
        paused: false,
        ended: true
      });
      setPlayState(currentPlayState);
      debug$3('onEnd [ID: %s]', videoId);
    }

    function onPlaying() {
      currentPlayState = _objectSpread$4(_objectSpread$4({}, currentPlayState), {}, {
        buffering: false
      });
      setPlayState(currentPlayState);
      debug$3('onPlaying [ID: %s]', videoId);
    }

    function onWaiting() {
      currentPlayState = _objectSpread$4(_objectSpread$4({}, currentPlayState), {}, {
        buffering: true
      });
      setPlayState(currentPlayState);
      debug$3('onWaiting [ID: %s]', videoId);
    }

    function onAdStart() {
      currentPlayState = _objectSpread$4(_objectSpread$4({}, currentPlayState), {}, {
        adPlaying: true
      });
      setPlayState(currentPlayState);
      debug$3('onAdStart [ID: %s]', videoId);
    }

    function onAdEnd() {
      currentPlayState = _objectSpread$4(_objectSpread$4({}, currentPlayState), {}, {
        adPlaying: false
      });
      setPlayState(currentPlayState);
      debug$3('onAdEnd [ID: %s]', videoId);
    }

    player.addEventListener('playback_ready', onPlaybackReady);
    player.addEventListener('loadedmetadata', onLoadedMetadata);
    player.addEventListener('durationchange', onDurationChange);
    player.addEventListener('volumechange', onVolumeChange);
    player.addEventListener('play', onPlay);
    player.addEventListener('pause', onPause);
    player.addEventListener('video_end', onEnd);
    player.addEventListener('playing', onPlaying);
    player.addEventListener('waiting', onWaiting);
    player.addEventListener('ad_start', onAdStart);
    player.addEventListener('ad_end', onAdEnd);
    return function () {
      player.removeEventListener('playback_ready', onPlaybackReady);
      player.removeEventListener('loadedmetadata', onLoadedMetadata);
      player.removeEventListener('durationchange', onDurationChange);
      player.removeEventListener('volumechange', onVolumeChange);
      player.removeEventListener('play', onPlay);
      player.removeEventListener('pause', onPause);
      player.removeEventListener('video_end', onEnd);
      player.removeEventListener('playing', onPlaying);
      player.removeEventListener('waiting', onWaiting);
      player.removeEventListener('ad_start', onAdStart);
      player.removeEventListener('ad_end', onAdEnd);
    };
  }, [playerRef.current, playerReady, videoId, setLoaded, setPlayState, setMetadata, setVolumeState, setMuted]);
  var play = useCallback(function () {
    var player = playerRef.current;
    return player !== null ? player.play() : Promise.reject(noPlayerError$1);
  }, []);
  var pause = useCallback(function () {
    var player = playerRef.current;
    return player !== null ? player.pause() : Promise.reject(noPlayerError$1);
  }, []);
  var setVolume = useCallback(function (newVolume) {
    var player = playerRef.current;
    return player !== null ? player.setVolume(newVolume) : Promise.reject(noPlayerError$1);
  }, []);
  var mute = useCallback(function () {
    var player = playerRef.current;
    return player !== null ? player.setMute(true) : Promise.reject(noPlayerError$1);
  }, []);
  var unmute = useCallback(function () {
    var player = playerRef.current;
    return player !== null ? player.setMute(false) : Promise.reject(noPlayerError$1);
  }, []);
  var seek = useCallback(function (time) {
    var player = playerRef.current;
    return player !== null ? player.seek(time) : Promise.reject(noPlayerError$1);
  }, []);
  var playing = playState.playing;
  var currentTime = usePlayerCurrentTime(playerRef.current, {
    id: videoId,
    disabled: !playing || timeUpdateInterval === null,
    updateInterval: timeUpdateInterval,
    onUpdate: customOnTimeUpdate
  });
  return _objectSpread$4(_objectSpread$4({
    ref: elementRef,
    player: playerRef.current,
    ready: ready,
    play: play,
    pause: pause,
    mute: mute,
    unmute: unmute,
    setVolume: setVolume,
    seek: seek,
    currentTime: currentTime,
    loaded: loaded,
    muted: muted,
    volume: volume
  }, metadata), playState);
};

var eventsManager$1 = typeof document !== 'undefined' ? new EventsManager(document) : null;

var useDocumentEvent = function useDocumentEvent(event, callback) {
  useEffect(function () {
    if (eventsManager$1 !== null && callback !== null) {
      eventsManager$1.subscribe(event, callback);
    }

    return function () {
      if (eventsManager$1 !== null && callback !== null) {
        eventsManager$1.unsubscribe(event, callback);
      }
    };
  }, [event, callback]);
};

var eventsManager = typeof window !== 'undefined' ? new EventsManager(window) : null;

var useWindowEvent = function useWindowEvent(event, callback) {
  useEffect(function () {
    if (eventsManager !== null && callback !== null) {
      eventsManager.subscribe(event, callback);
    }

    return function () {
      if (eventsManager !== null && callback !== null) {
        eventsManager.unsubscribe(event, callback);
      }
    };
  }, [event, callback]);
};

function useKeyboard() {
  var keyMap = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
  var onKeyDown = useCallback(function (event) {
    var key = event.key;
    var callback = null;

    if (typeof keyMap === 'function') {
      callback = keyMap;
    } else if (typeof keyMap[key] !== 'undefined') {
      callback = typeof keyMap[key] === 'function' ? keyMap[key] : (keyMap[key] || {}).down || null;
    }

    if (callback !== null) {
      callback(event);
    }
  }, [keyMap]);
  var onKeyUp = useCallback(function (event) {
    var key = event.key;
    var callback = null;

    if (typeof keyMap === 'function') {
      callback = keyMap;
    } else if (typeof keyMap[key] !== 'undefined') {
      callback = typeof keyMap[key] === 'function' ? keyMap[key] : (keyMap[key] || {}).up || null;
    }

    if (callback !== null) {
      callback(event);
    }
  }, [keyMap]);
  useWindowEvent('keydown', keyMap !== null ? onKeyDown : null);
  useWindowEvent('keyup', keyMap !== null ? onKeyUp : null);
}

function ownKeys$3(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$3(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys$3(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys$3(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
var debug$2 = createDebug('folklore:video:native');
var noPlayerError = new Error('No player');

var useNativeVideoPlayer = function useNativeVideoPlayer(url) {
  var _ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
      _ref$width = _ref.width,
      width = _ref$width === void 0 ? 0 : _ref$width,
      _ref$height = _ref.height,
      height = _ref$height === void 0 ? 0 : _ref$height,
      _ref$duration = _ref.duration,
      duration = _ref$duration === void 0 ? 0 : _ref$duration,
      _ref$initialMuted = _ref.initialMuted,
      initialMuted = _ref$initialMuted === void 0 ? false : _ref$initialMuted,
      _ref$timeUpdateInterv = _ref.timeUpdateInterval,
      timeUpdateInterval = _ref$timeUpdateInterv === void 0 ? 1000 : _ref$timeUpdateInterv,
      _ref$onTimeUpdate = _ref.onTimeUpdate,
      customOnTimeUpdate = _ref$onTimeUpdate === void 0 ? null : _ref$onTimeUpdate;

  var elementRef = useRef(null);

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      loaded = _useState2[0],
      setLoaded = _useState2[1];

  var _useState3 = useState(initialMuted ? 0 : 1),
      _useState4 = _slicedToArray(_useState3, 2),
      volume = _useState4[0],
      setVolumeState = _useState4[1];

  var _useState5 = useState({
    playing: false,
    paused: false,
    ended: false,
    buffering: false
  }),
      _useState6 = _slicedToArray(_useState5, 2),
      playState = _useState6[0],
      setPlayState = _useState6[1];

  var _useState7 = useState({
    width: width,
    height: height,
    duration: duration
  }),
      _useState8 = _slicedToArray(_useState7, 2),
      metadata = _useState8[0],
      setMetadata = _useState8[1];

  var play = useCallback(function () {
    var player = elementRef.current;
    return player !== null ? player.play() : Promise.reject(noPlayerError);
  }, []);
  var pause = useCallback(function () {
    var player = elementRef.current;
    return player !== null ? player.pause() : Promise.reject(noPlayerError);
  }, []);
  var setVolume = useCallback(function (newVolume) {
    var player = elementRef.current;

    if (player !== null) {
      player.volume = newVolume;
      return Promise.resolve(newVolume);
    }

    return Promise.reject(noPlayerError);
  }, []);
  var mute = useCallback(function () {
    var player = elementRef.current;

    if (player !== null) {
      player.muted = true;
      return Promise.resolve(true);
    }

    return Promise.reject(noPlayerError);
  }, []);
  var unmute = useCallback(function () {
    var player = elementRef.current;

    if (player !== null) {
      player.muted = false;
      return Promise.resolve(false);
    }

    return Promise.reject(noPlayerError);
  }, []);
  var seek = useCallback(function (newTime) {
    var player = elementRef.current;

    if (player !== null) {
      player.currentTime = newTime;
      return Promise.resolve(newTime);
    }

    return Promise.reject(noPlayerError);
  }, []);
  var setLoop = useCallback(function (newLoop) {
    var player = elementRef.current;

    if (player !== null) {
      player.loop = newLoop;
      return Promise.resolve(newLoop);
    }

    return Promise.reject(noPlayerError);
  }, []); // Bind player events

  useEffect(function () {
    var player = elementRef.current;

    if (player === null) {
      return function () {};
    }

    var onCanPlay = function onCanPlay() {
      setLoaded(true);
      debug$2('onCanPlay [URL: %s]', url);
    };

    var onMetadataLoaded = function onMetadataLoaded() {
      var newMetadata = {
        duration: player.duration,
        width: player.videoWidth,
        height: player.videoHeight
      };
      setMetadata(newMetadata);
      debug$2('onMetadataLoaded [URL: %s]', url);
    };

    var onPlay = function onPlay() {
      setPlayState({
        playing: true,
        paused: false,
        ended: false,
        buffering: false
      });
      debug$2('onPlay [URL: %s]', url);
    };

    var onPause = function onPause() {
      setPlayState({
        playing: false,
        paused: true,
        ended: false,
        buffering: false
      });
      debug$2('onPause [URL: %s]', url);
    };

    var onEnded = function onEnded() {
      setPlayState({
        playing: false,
        paused: false,
        ended: true,
        buffering: false
      });
      debug$2('onEnded [URL: %s]', url);
    };

    debug$2('Bind events [URL: %s]', url);
    player.addEventListener('canplay', onCanPlay);
    player.addEventListener('metadataloaded', onMetadataLoaded);
    player.addEventListener('play', onPlay);
    player.addEventListener('pause', onPause);
    player.addEventListener('ended', onEnded);
    return function () {
      debug$2('Unbind events [URL: %s]', url);
      player.removeEventListener('canplay', onCanPlay);
      player.removeEventListener('metadataloaded', onMetadataLoaded);
      player.removeEventListener('play', onPlay);
      player.removeEventListener('pause', onPause);
      player.removeEventListener('ended', onEnded);
    };
  }, [url, elementRef.current, setPlayState, setMetadata, setVolumeState, setLoaded]);
  var playing = playState.playing;
  var currentTime = usePlayerCurrentTime(elementRef.current, {
    id: url,
    disabled: !playing || timeUpdateInterval === null,
    updateInterval: timeUpdateInterval,
    onUpdate: customOnTimeUpdate
  });
  return _objectSpread$3(_objectSpread$3({
    ref: elementRef,
    player: elementRef.current,
    play: play,
    pause: pause,
    mute: mute,
    unmute: unmute,
    setVolume: setVolume,
    seek: seek,
    setLoop: setLoop,
    ready: true,
    currentTime: currentTime,
    loaded: loaded,
    muted: volume === 0,
    volume: volume
  }, metadata), playState);
};

function ownKeys$2(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$2(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys$2(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys$2(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
var observersCache = new Map();

var getOptionsKey = function getOptionsKey(_ref) {
  var _ref$root = _ref.root,
      root = _ref$root === void 0 ? null : _ref$root,
      rootMargin = _ref.rootMargin,
      _ref$threshold = _ref.threshold,
      threshold = _ref$threshold === void 0 ? null : _ref$threshold;
  return "root_".concat(root, "_rootMargin_").concat(rootMargin || null, "_threshold_").concat(threshold);
};

var createObserver = function createObserver(Observer) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var subscribers = [];

  var addSubscriber = function addSubscriber(element, callback) {
    var currentSubscriber = subscribers.find(function (it) {
      return it.element === element;
    }) || null;

    if (currentSubscriber !== null) {
      return subscribers.map(function (it) {
        return it.element === element && it.callbacks.indexOf(callback) === -1 ? _objectSpread$2(_objectSpread$2({}, it), {}, {
          callbacks: [].concat(_toConsumableArray(it.callbacks), [callback])
        }) : it;
      }).filter(function (it) {
        return it.callbacks.length > 0;
      });
    }

    return [].concat(_toConsumableArray(subscribers), [{
      element: element,
      callbacks: [callback]
    }]);
  };

  var removeSubscriber = function removeSubscriber(element, callback) {
    return subscribers.map(function (it) {
      return it.element === element ? _objectSpread$2(_objectSpread$2({}, it), {}, {
        callbacks: it.callbacks.filter(function (subCallback) {
          return subCallback !== callback;
        })
      }) : it;
    }).filter(function (it) {
      return it.callbacks.length > 0;
    });
  };

  var onUpdate = function onUpdate(entries) {
    entries.forEach(function (entry) {
      subscribers.forEach(function (_ref2) {
        var element = _ref2.element,
            callbacks = _ref2.callbacks;

        if (element === entry.target) {
          callbacks.forEach(function (callback) {
            callback(entry);
          });
        }
      });
    });
  };

  var observer = new Observer(onUpdate, options);

  var unsubscribe = function unsubscribe(element) {
    var callback = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
    subscribers = removeSubscriber(element, callback);

    if (typeof observer.unobserve === 'undefined') {
      observer.disconnect();
      subscribers.forEach(function (subscriber) {
        observer.observe(subscriber.element);
      });
      return;
    }

    var currentSubscriber = subscribers.find(function (it) {
      return it.element === element;
    }) || null;

    if (currentSubscriber === null) {
      observer.unobserve(element);
    }
  };

  var subscribe = function subscribe(element, callback) {
    var currentSubscriber = subscribers.find(function (it) {
      return it.element === element;
    }) || null;
    subscribers = addSubscriber(element, callback);

    if (currentSubscriber === null) {
      observer.observe(element);
    }
  };

  return {
    subscribe: subscribe,
    unsubscribe: unsubscribe,
    observer: observer
  };
};

var getObserver = function getObserver(Observer) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var observerKey = getOptionsKey(options);

  if (!observersCache.has(Observer)) {
    observersCache.set(Observer, {});
  }

  var observers = observersCache.get(Observer);

  if (typeof observers[observerKey] === 'undefined') {
    observers[observerKey] = createObserver(Observer, options);
    observersCache.set(Observer, observers);
  }

  return observers[observerKey];
};
var useObserver = function useObserver(Observer) {
  var opts = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var initialEntry = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  var _opts$root = opts.root,
      root = _opts$root === void 0 ? null : _opts$root,
      _opts$rootMargin = opts.rootMargin,
      rootMargin = _opts$rootMargin === void 0 ? null : _opts$rootMargin,
      _opts$threshold = opts.threshold,
      threshold = _opts$threshold === void 0 ? null : _opts$threshold,
      _opts$disabled = opts.disabled,
      disabled = _opts$disabled === void 0 ? false : _opts$disabled;

  var _useState = useState(initialEntry),
      _useState2 = _slicedToArray(_useState, 2),
      entry = _useState2[0],
      setEntry = _useState2[1];

  var nodeRef = useRef(null);
  var currentElement = useRef(null);
  var elementChanged = nodeRef.current !== currentElement.current;
  useEffect(function () {
    var nodeElement = nodeRef.current;

    var callback = function callback(newEntry) {
      return setEntry(newEntry);
    };

    var unsubscribe = null;

    if (nodeElement !== null) {
      var newOpts = {};

      if (root !== null) {
        newOpts.root = root;
      }

      if (rootMargin !== null) {
        newOpts.rootMargin = rootMargin;
      }

      if (threshold !== null) {
        newOpts.threshold = threshold;
      }

      var _getObserver = getObserver(Observer, newOpts),
          subscribe = _getObserver.subscribe,
          localUnsubscribe = _getObserver.unsubscribe;

      unsubscribe = localUnsubscribe;
      subscribe(nodeElement, callback);
    }

    currentElement.current = nodeElement;
    return function () {
      if (unsubscribe !== null) {
        unsubscribe(nodeElement, callback);
      }
    };
  }, [Observer, elementChanged, disabled, root, rootMargin, threshold]);
  return {
    ref: nodeRef,
    entry: entry
  };
};
/**
 * Intersection Observer
 */

var intersectionObserverInitialEntry = {
  target: null,
  time: null,
  isVisible: false,
  isIntersecting: false,
  intersectionRatio: 0,
  intersectionRect: null,
  boundingClientRect: null,
  rootBounds: null
};
var useIntersectionObserver = function useIntersectionObserver() {
  var _ref3 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      _ref3$root = _ref3.root,
      root = _ref3$root === void 0 ? null : _ref3$root,
      _ref3$rootMargin = _ref3.rootMargin,
      rootMargin = _ref3$rootMargin === void 0 ? '0px' : _ref3$rootMargin,
      _ref3$threshold = _ref3.threshold,
      threshold = _ref3$threshold === void 0 ? [0, 1.0] : _ref3$threshold,
      _ref3$disabled = _ref3.disabled,
      disabled = _ref3$disabled === void 0 ? false : _ref3$disabled;

  return useObserver(IntersectionObserver, {
    root: root,
    rootMargin: rootMargin,
    threshold: threshold,
    disabled: disabled
  }, intersectionObserverInitialEntry);
};
/**
 * Resize Observer
 */

var resizeObserverInitialEntry = {
  target: null,
  contentRect: null,
  contentBoxSize: null,
  borderBoxSize: null
};
var useResizeObserver = function useResizeObserver() {
  var _ref4 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      _ref4$disabled = _ref4.disabled,
      disabled = _ref4$disabled === void 0 ? false : _ref4$disabled;

  return useObserver(ResizeObserver, {
    disabled: disabled
  }, resizeObserverInitialEntry);
};

function ownKeys$1(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$1(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys$1(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys$1(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
var NO_PLAYER_ERROR$1 = new Error('No player');
var debug$1 = createDebug('folklore:video:youtube');

function useYouTubePlayer(id) {
  var _ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
      _ref$width = _ref.width,
      width = _ref$width === void 0 ? 0 : _ref$width,
      _ref$height = _ref.height,
      height = _ref$height === void 0 ? 0 : _ref$height,
      _ref$duration = _ref.duration,
      duration = _ref$duration === void 0 ? 0 : _ref$duration,
      _ref$autoplay = _ref.autoplay,
      autoplay = _ref$autoplay === void 0 ? false : _ref$autoplay,
      _ref$controls = _ref.controls,
      controls = _ref$controls === void 0 ? true : _ref$controls,
      _ref$timeUpdateInterv = _ref.timeUpdateInterval,
      timeUpdateInterval = _ref$timeUpdateInterv === void 0 ? 1000 : _ref$timeUpdateInterv,
      _ref$muted = _ref.muted,
      initialMuted = _ref$muted === void 0 ? false : _ref$muted,
      _ref$onVolumeChange = _ref.onVolumeChange,
      customOnVolumeChange = _ref$onVolumeChange === void 0 ? null : _ref$onVolumeChange,
      _ref$onTimeUpdate = _ref.onTimeUpdate,
      customOnTimeUpdate = _ref$onTimeUpdate === void 0 ? null : _ref$onTimeUpdate,
      _ref$getVideoId = _ref.getVideoId,
      getVideoId = _ref$getVideoId === void 0 ? function (url) {
    if (url === null || url.match(/^https?:/) === null) {
      return url;
    }

    var regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
    var match = url.match(regExp);
    return match && match[7].length === 11 ? match[7] : null;
  } : _ref$getVideoId;

  var _useState = useState(typeof window.YT !== 'undefined'),
      _useState2 = _slicedToArray(_useState, 2),
      apiLoaded = _useState2[0],
      setApiLoaded = _useState2[1];

  var apiRef = useRef(typeof window.YT !== 'undefined' ? window.YT : null);
  var elementRef = useRef(null);
  var playerRef = useRef(null);
  var playerElementRef = useRef(elementRef.current);
  var elementHasChanged = elementRef.current !== playerElementRef.current;
  var videoId = useMemo(function () {
    return getVideoId(id);
  }, [id]);

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      ready = _useState4[0],
      setReady = _useState4[1];

  var _useState5 = useState(initialMuted),
      _useState6 = _slicedToArray(_useState5, 2),
      muted = _useState6[0],
      setMuted = _useState6[1];

  var _useState7 = useState({
    playing: false,
    paused: false,
    ended: false,
    buffering: false
  }),
      _useState8 = _slicedToArray(_useState7, 2),
      playState = _useState8[0],
      setPlayState = _useState8[1];

  var _useState9 = useState({
    width: width,
    height: height,
    duration: duration
  }),
      _useState10 = _slicedToArray(_useState9, 2),
      metadata = _useState10[0],
      setMetadata = _useState10[1];

  useEffect(function () {
    var canceled = false;

    if (!apiLoaded && videoId !== null) {
      debug$1('Load API');
      loadYouTube().then(function (api) {
        if (!canceled) {
          apiRef.current = api;
          setApiLoaded(true);
          debug$1('API Loaded');
        }
      });
    }

    return function () {
      canceled = true;
    };
  }, [apiLoaded, videoId, setApiLoaded]);
  var play = useCallback(function () {
    var player = playerRef.current;
    return player !== null && typeof player.playVideo !== 'undefined' ? Promise.resolve(player.playVideo()) : Promise.reject(NO_PLAYER_ERROR$1);
  }, []);
  var pause = useCallback(function () {
    var player = playerRef.current;
    return player !== null && typeof player.pauseVideo !== 'undefined' ? Promise.resolve(player.pauseVideo()) : Promise.reject(NO_PLAYER_ERROR$1);
  }, []);
  var setVolume = useCallback(function (volume) {
    var player = playerRef.current;
    var promise = player !== null && typeof player.setVolume !== 'undefined' ? Promise.resolve(player.setVolume(volume * 100)) : Promise.reject(NO_PLAYER_ERROR$1);

    if (customOnVolumeChange) {
      customOnVolumeChange(volume);
    }

    return promise;
  }, [customOnVolumeChange]);
  var mute = useCallback(function () {
    var player = playerRef.current;
    return (player !== null && typeof player.mute !== 'undefined' ? Promise.resolve(player.mute()) : Promise.reject(NO_PLAYER_ERROR$1)).then(function () {
      return setMuted(true);
    });
  }, [setMuted]);
  var unmute = useCallback(function () {
    var player = playerRef.current;
    return (player !== null && typeof player.unMute !== 'undefined' ? Promise.resolve(player.unMute()) : Promise.reject(NO_PLAYER_ERROR$1)).then(function () {
      return setMuted(false);
    });
  }, []);
  var seek = useCallback(function (time) {
    var player = playerRef.current;
    return player !== null && typeof player.seekTo !== 'undefined' ? Promise.resolve(player.seekTo(time)) : Promise.reject(NO_PLAYER_ERROR$1);
  }, []);
  var setLoop = useCallback(function (loop) {
    var player = playerRef.current;
    return player !== null && typeof player.setLoop !== 'undefined' ? Promise.resolve(player.setLoop(loop)) : Promise.reject(NO_PLAYER_ERROR$1);
  }, []);
  var destroyPlayer = useCallback(function () {
    if (playerRef.current !== null) {
      debug$1('Unset player');
      playerRef.current = null;
    }
  }, []); // Detect iframe switch and destroy player

  useEffect(function () {
    var currentPlayer = playerRef.current;

    if (playerElementRef.current !== elementRef.current && currentPlayer !== null) {
      debug$1('iFrame switched');
      destroyPlayer();
    }
  }, [elementHasChanged]); // Create player

  useEffect(function () {
    var _apiRef$current = apiRef.current,
        YT = _apiRef$current === void 0 ? null : _apiRef$current;
    var _playerRef$current = playerRef.current,
        currentPlayer = _playerRef$current === void 0 ? null : _playerRef$current;
    var _elementRef$current = elementRef.current,
        element = _elementRef$current === void 0 ? null : _elementRef$current;

    if (!apiLoaded || videoId === null || element === null) {
      destroyPlayer();
      return;
    }

    var player = currentPlayer;

    if (player !== null && typeof player.loadVideoById !== 'undefined') {
      debug$1('Switch video [ID: %s]', videoId);
      player.loadVideoById(videoId);
    } else {
      debug$1('Create player [ID: %s]', videoId);

      var onReady = function onReady(_ref2) {
        var target = _ref2.target;
        player = target;
        playerRef.current = target;
        setReady(true);
        var newDuration = target.getDuration();

        if (newDuration !== metadata.duration) {
          setMetadata(_objectSpread$1(_objectSpread$1({}, metadata), {}, {
            duration: newDuration
          }));
        }

        debug$1('onReady [ID: %s]', videoId);
      };

      var onStateChange = function onStateChange(_ref3) {
        var state = _ref3.data;
        var newState = {
          playing: state === YT.PlayerState.PLAYING,
          paused: state === YT.PlayerState.PAUSED,
          ended: state === YT.PlayerState.ENDED,
          buffering: state === YT.PlayerState.BUFFERING
        };
        setPlayState(newState);
        var stateLabel = null;

        if (newState.playing) {
          stateLabel = 'playing';
        } else if (newState.paused) {
          stateLabel = 'paused';
        } else if (newState.ended) {
          stateLabel = 'ended';
        } else if (newState.buffering) {
          stateLabel = 'buffering';
        } else if (state === -1) {
          stateLabel = 'not started';
        } else if (state === 0) {
          stateLabel = 'stopped';
        }

        debug$1('onStateChange %s [ID: %s]', stateLabel, videoId);
      };

      player = new YT.Player(element, {
        videoId: videoId,
        playerVars: {
          controls: controls,
          autoplay: autoplay ? 1 : 0,
          mute: initialMuted,
          playsinline: true,
          rel: 0,
          showinfo: 0,
          modestbranding: 1
        },
        events: {
          onReady: onReady,
          onStateChange: onStateChange
        }
      });
    }

    playerRef.current = player;
    playerElementRef.current = element;
  }, [apiLoaded, videoId, elementHasChanged, setPlayState, setReady, setMetadata, destroyPlayer]);
  var playing = playState.playing;
  var getCurrentTime = useCallback(function (p) {
    return p.getCurrentTime();
  }, []);
  var currentTime = usePlayerCurrentTime(playerRef.current, {
    id: videoId,
    disabled: !playing || timeUpdateInterval === null,
    updateInterval: timeUpdateInterval,
    onUpdate: customOnTimeUpdate,
    getCurrentTime: getCurrentTime
  });
  return _objectSpread$1(_objectSpread$1({
    ref: elementRef,
    player: playerRef.current,
    play: play,
    pause: pause,
    mute: mute,
    unmute: unmute,
    setVolume: setVolume,
    seek: seek,
    setLoop: setLoop,
    ready: ready,
    currentTime: currentTime,
    muted: muted,
    loaded: ready
  }, metadata), playState);
}

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
var debug = createDebug('folklore:video:vimeo');
var NO_PLAYER_ERROR = new Error('No player');

var useVimeoPlayer = function useVimeoPlayer(id) {
  var _ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
      _ref$width = _ref.width,
      width = _ref$width === void 0 ? 0 : _ref$width,
      _ref$height = _ref.height,
      height = _ref$height === void 0 ? 0 : _ref$height,
      _ref$duration = _ref.duration,
      duration = _ref$duration === void 0 ? 0 : _ref$duration,
      _ref$autoplay = _ref.autoplay,
      autoplay = _ref$autoplay === void 0 ? false : _ref$autoplay,
      _ref$autopause = _ref.autopause,
      autopause = _ref$autopause === void 0 ? true : _ref$autopause,
      _ref$byline = _ref.byline,
      byline = _ref$byline === void 0 ? false : _ref$byline,
      _ref$controls = _ref.controls,
      controls = _ref$controls === void 0 ? false : _ref$controls,
      _ref$initialMuted = _ref.initialMuted,
      initialMuted = _ref$initialMuted === void 0 ? false : _ref$initialMuted,
      _ref$timeUpdateInterv = _ref.timeUpdateInterval,
      timeUpdateInterval = _ref$timeUpdateInterv === void 0 ? 1000 : _ref$timeUpdateInterv,
      _ref$onTimeUpdate = _ref.onTimeUpdate,
      customOnTimeUpdate = _ref$onTimeUpdate === void 0 ? null : _ref$onTimeUpdate,
      _ref$getVideoId = _ref.getVideoId,
      getVideoId = _ref$getVideoId === void 0 ? function (url) {
    if (url === null || url.match(/^[0-9]+$/) !== null) {
      return url;
    }

    var match = url.match(/\/[0-9]+/);
    return match !== null ? match[1] : null;
  } : _ref$getVideoId;

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      apiLoaded = _useState2[0],
      setApiLoaded = _useState2[1];

  var apiRef = useRef(null);
  var elementRef = useRef(null);
  var playerRef = useRef(null);
  var playerElementRef = useRef(elementRef.current);
  var elementHasChanged = elementRef.current !== playerElementRef.current;
  var videoId = useMemo(function () {
    return getVideoId(id);
  }, [id]);

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      ready = _useState4[0],
      setReady = _useState4[1];

  var _useState5 = useState(false),
      _useState6 = _slicedToArray(_useState5, 2),
      loaded = _useState6[0],
      setLoaded = _useState6[1];

  var _useState7 = useState(initialMuted ? 0 : 1),
      _useState8 = _slicedToArray(_useState7, 2),
      volume = _useState8[0],
      setVolumeState = _useState8[1];

  var _useState9 = useState({
    playing: false,
    paused: false,
    ended: false,
    buffering: false
  }),
      _useState10 = _slicedToArray(_useState9, 2),
      playState = _useState10[0],
      setPlayState = _useState10[1];

  var _useState11 = useState({
    width: width,
    height: height,
    duration: duration
  }),
      _useState12 = _slicedToArray(_useState11, 2),
      metadata = _useState12[0],
      setMetadata = _useState12[1]; // Load SDK


  useEffect(function () {
    var canceled = false;

    if (!apiLoaded && id !== null) {
      debug('Load API');
      loadVimeo().then(function (api) {
        if (!canceled) {
          apiRef.current = api;
          setApiLoaded(true);
          debug('API Loaded');
        }
      });
    }

    return function () {
      canceled = true;
    };
  }, [id]);
  var play = useCallback(function () {
    var player = playerRef.current;
    return player !== null ? player.play() : Promise.reject(NO_PLAYER_ERROR);
  }, []);
  var pause = useCallback(function () {
    var player = playerRef.current;
    return player !== null ? player.pause() : Promise.reject(NO_PLAYER_ERROR);
  }, []);
  var setVolume = useCallback(function (newVolume) {
    var player = playerRef.current;
    return player !== null ? player.setVolume(newVolume) : Promise.reject(NO_PLAYER_ERROR);
  }, []);
  var mute = useCallback(function () {
    var player = playerRef.current;
    return player !== null ? player.setVolume(0) : Promise.reject(NO_PLAYER_ERROR);
  }, []);
  var unmute = useCallback(function () {
    var player = playerRef.current;
    return player !== null ? player.setVolume(1) : Promise.reject(NO_PLAYER_ERROR);
  }, []);
  var seek = useCallback(function (time) {
    var player = playerRef.current;
    return player !== null ? player.setCurrentTime(time) : Promise.reject(NO_PLAYER_ERROR);
  }, []);
  var setLoop = useCallback(function (loop) {
    var player = playerRef.current;
    return player !== null ? player.setLoop(loop) : Promise.reject(NO_PLAYER_ERROR);
  }, []);
  var destroyVideo = useCallback(function () {
    var player = playerRef.current;

    if (player !== null) {
      debug('Unload video');
      player.unload();
    }

    if (player !== null) {
      debug('Unset video');
      playerRef.current = null;
    }
  }, []);
  useEffect(function () {
    var currentPlayer = playerRef.current;

    if (playerElementRef.current !== elementRef.current && currentPlayer !== null) {
      debug('iFrame switched');
      destroyVideo();
    }
  }, [elementHasChanged]); // Create player

  useEffect(function () {
    var _apiRef$current = apiRef.current,
        Player = _apiRef$current === void 0 ? null : _apiRef$current;
    var _playerRef$current = playerRef.current,
        currentPlayer = _playerRef$current === void 0 ? null : _playerRef$current;
    var _elementRef$current = elementRef.current,
        element = _elementRef$current === void 0 ? null : _elementRef$current;

    if (!apiLoaded || videoId === null || element === null) {
      destroyVideo();
      return;
    }

    var player = currentPlayer;

    if (player === null) {
      debug('Create player [ID: %s]', videoId);
      player = new Player(element, {
        id: videoId,
        autoplay: autoplay,
        autopause: autopause,
        byline: byline,
        controls: controls,
        muted: initialMuted,
        background: !controls
      });
      player.ready().then(function () {
        return setReady(true);
      }).catch(function (e) {
        debug('ERROR: %o', e);
      });
    } else {
      debug('Load video [ID: %s]', videoId);
      player.loadVideo(videoId).catch(function (e) {
        debug('ERROR: %o', e);
      });
    }

    playerRef.current = player;
    playerElementRef.current = element;
  }, [apiLoaded, videoId, elementHasChanged, setReady, destroyVideo, setLoaded]); // Bind player events

  useEffect(function () {
    var player = playerRef.current;

    if (player === null) {
      return function () {};
    }

    var canceled = false;

    var onLoaded = function onLoaded() {
      Promise.all([player.getDuration(), player.getVideoWidth(), player.getVideoHeight(), player.getMuted()]).then(function (_ref2) {
        var _ref3 = _slicedToArray(_ref2, 4),
            newDuration = _ref3[0],
            newWidth = _ref3[1],
            newHeight = _ref3[2],
            newMuted = _ref3[3];

        if (canceled) {
          return;
        }

        var newMetadata = {
          duration: newDuration,
          width: newWidth,
          height: newHeight
        };
        setMetadata(newMetadata);

        if (newMuted) {
          setVolumeState(0);
        }

        setLoaded(true);
      });
      debug('onLoaded [ID: %s]', videoId);
    };

    var onPlay = function onPlay() {
      setPlayState({
        playing: true,
        paused: false,
        ended: false,
        buffering: false
      });
      debug('onPlay [ID: %s]', videoId);
      player.getMuted().then(function (newMuted) {
        if (!canceled && newMuted) {
          setVolumeState(0);
        }
      }).catch(function () {});
    };

    var onPause = function onPause() {
      setPlayState({
        playing: false,
        paused: true,
        ended: false,
        buffering: false
      });
      debug('onPause [ID: %s]', videoId);
    };

    var onVolumeChange = function onVolumeChange(_ref4) {
      var newVolume = _ref4.volume;
      setVolumeState(newVolume);
      debug('onVolumeChange [ID: %s]', videoId);
    };

    var onEnded = function onEnded() {
      setPlayState({
        playing: false,
        paused: false,
        ended: true,
        buffering: false
      });
      debug('onEnded [ID: %s]', videoId);
    };

    var onBufferStart = function onBufferStart() {
      setPlayState({
        playing: true,
        paused: false,
        ended: false,
        buffering: true
      });
      debug('onBufferStart [ID: %s]', videoId);
    };

    var onBufferEnded = function onBufferEnded() {
      setPlayState({
        playing: true,
        paused: false,
        ended: false,
        buffering: false
      });
      debug('onBufferStart [ID: %s]', videoId);
    };

    debug('Bind events [ID: %s]', videoId);
    player.on('loaded', onLoaded);
    player.on('play', onPlay);
    player.on('pause', onPause);
    player.on('volumechange', onVolumeChange);
    player.on('ended', onEnded);
    player.on('bufferstart', onBufferStart);
    player.on('bufferend', onBufferEnded);
    return function () {
      canceled = true;
      debug('Unbind events [ID: %s]', videoId);
      player.off('loaded', onLoaded);
      player.off('play', onPlay);
      player.off('pause', onPause);
      player.off('volumechange', onVolumeChange);
      player.off('ended', onEnded);
      player.off('bufferstart', onBufferStart);
      player.off('bufferend', onBufferEnded);
    };
  }, [videoId, playerRef.current, setPlayState, setMetadata, setVolumeState, setLoaded]);
  var playing = playState.playing;
  var getCurrentTime = useCallback(function (p) {
    return p.getCurrentTime();
  }, []);
  var currentTime = usePlayerCurrentTime(playerRef.current, {
    id: videoId,
    disabled: !playing || timeUpdateInterval === null,
    updateInterval: timeUpdateInterval,
    onUpdate: customOnTimeUpdate,
    getCurrentTime: getCurrentTime
  });
  return _objectSpread(_objectSpread({
    ref: elementRef,
    player: playerRef.current,
    play: play,
    pause: pause,
    mute: mute,
    unmute: unmute,
    setVolume: setVolume,
    seek: seek,
    setLoop: setLoop,
    ready: ready,
    currentTime: currentTime,
    loaded: loaded,
    muted: volume === 0,
    volume: volume
  }, metadata), playState);
};

function useVideoPlayer(params) {
  var _ref = params || {},
      _ref$service = _ref.service,
      service = _ref$service === void 0 ? null : _ref$service,
      _ref$videoId = _ref.videoId,
      videoId = _ref$videoId === void 0 ? null : _ref$videoId,
      _ref$url = _ref.url,
      url = _ref$url === void 0 ? null : _ref$url,
      _ref$onLoaded = _ref.onLoaded,
      customOnLoaded = _ref$onLoaded === void 0 ? null : _ref$onLoaded,
      _ref$onPlay = _ref.onPlay,
      customOnPlay = _ref$onPlay === void 0 ? null : _ref$onPlay,
      _ref$onPause = _ref.onPause,
      customOnPause = _ref$onPause === void 0 ? null : _ref$onPause,
      _ref$onEnd = _ref.onEnd,
      customOnEnd = _ref$onEnd === void 0 ? null : _ref$onEnd,
      _ref$onMetadataChange = _ref.onMetadataChange,
      customOnMetadataChange = _ref$onMetadataChange === void 0 ? null : _ref$onMetadataChange,
      _ref$onBufferStart = _ref.onBufferStart,
      customOnBufferStart = _ref$onBufferStart === void 0 ? null : _ref$onBufferStart,
      _ref$onBufferEnded = _ref.onBufferEnded,
      customOnBufferEnded = _ref$onBufferEnded === void 0 ? null : _ref$onBufferEnded;

  var dailymotionPlayer = useDailymotionPlayer(service === 'dailymotion' ? videoId || url : null, params);
  var youtubePlayer = useYouTubePlayer(service === 'youtube' ? videoId || url : null, params);
  var vimeoPlayer = useVimeoPlayer(service === 'vimeo' ? videoId || url : null, params);
  var nativePlayer = useNativeVideoPlayer(url, params);
  var player = null;

  if (service === 'dailymotion') {
    player = dailymotionPlayer;
  } else if (service === 'youtube') {
    player = youtubePlayer;
  } else if (service === 'vimeo') {
    player = vimeoPlayer;
  } else {
    player = nativePlayer;
  }

  var _ref2 = player || {},
      _ref2$playing = _ref2.playing,
      playing = _ref2$playing === void 0 ? false : _ref2$playing,
      _ref2$paused = _ref2.paused,
      paused = _ref2$paused === void 0 ? false : _ref2$paused,
      _ref2$buffering = _ref2.buffering,
      buffering = _ref2$buffering === void 0 ? false : _ref2$buffering,
      _ref2$ended = _ref2.ended,
      ended = _ref2$ended === void 0 ? false : _ref2$ended,
      _ref2$ready = _ref2.ready,
      ready = _ref2$ready === void 0 ? false : _ref2$ready,
      _ref2$width = _ref2.width,
      metaWidth = _ref2$width === void 0 ? null : _ref2$width,
      _ref2$height = _ref2.height,
      metaHeight = _ref2$height === void 0 ? null : _ref2$height,
      _ref2$duration = _ref2.duration,
      metaDuration = _ref2$duration === void 0 ? null : _ref2$duration;

  useEffect(function () {
    if (ready && customOnLoaded !== null) {
      customOnLoaded();
    }
  }, [ready, customOnLoaded]);
  useEffect(function () {
    if (playing && customOnPlay !== null) {
      customOnPlay();
    }
  }, [playing, customOnPlay]);
  useEffect(function () {
    if (paused && customOnPause !== null) {
      customOnPause();
    }
  }, [paused, customOnPause]);
  useEffect(function () {
    if (buffering && customOnBufferStart !== null) {
      customOnBufferStart();
    } else if (!buffering && customOnBufferEnded !== null) {
      customOnBufferEnded();
    }
  }, [buffering, customOnBufferStart, customOnBufferEnded]);
  useEffect(function () {
    if (ended && customOnEnd !== null) {
      customOnEnd();
    }
  }, [ended, customOnEnd]);
  useEffect(function () {
    var hasMetadata = metaWidth !== null || metaHeight !== null || metaDuration !== null;

    if (hasMetadata && customOnMetadataChange !== null) {
      customOnMetadataChange({
        width: metaWidth,
        height: metaHeight,
        duration: metaDuration
      });
    }
  }, [metaWidth, metaHeight, metaDuration, customOnMetadataChange]);
  return player;
}

var getWindowScroll = function getWindowScroll() {
  return {
    x: typeof window !== 'undefined' ? window.scrollX || 0 : 0,
    y: typeof window !== 'undefined' ? window.scrollY || 0 : 0
  };
};

var currentScroll = getWindowScroll();

var useWindowScroll = function useWindowScroll() {
  var opts = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var _opts$onChange = opts.onChange,
      onChange = _opts$onChange === void 0 ? null : _opts$onChange;

  var _useState = useState(currentScroll),
      _useState2 = _slicedToArray(_useState, 2),
      scroll = _useState2[0],
      setScroll = _useState2[1];

  var updateScroll = useCallback(function () {
    var newScroll = getWindowScroll();

    if (currentScroll.x !== newScroll.x || currentScroll.y !== newScroll.y) {
      currentScroll.x = newScroll.x;
      currentScroll.y = newScroll.y;
      setScroll(newScroll);
      return newScroll;
    }

    return null;
  }, [setScroll]);
  var onScroll = useCallback(function () {
    var newScroll = updateScroll();

    if (newScroll !== null && onChange !== null) {
      onChange(newScroll);
    }
  }, [updateScroll, onChange]);
  useWindowEvent('scroll', onScroll);
  useEffect(function () {
    onScroll();
  }, []);
  return scroll;
};

var getWindowSize = function getWindowSize() {
  return {
    width: typeof window !== 'undefined' ? window.innerWidth || 0 : 0,
    height: typeof window !== 'undefined' ? window.innerHeight || 0 : 0
  };
};
var currentSize = getWindowSize();

function useWindowSize() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      _ref$onChange = _ref.onChange,
      onChange = _ref$onChange === void 0 ? null : _ref$onChange;

  var _useState = useState(currentSize),
      _useState2 = _slicedToArray(_useState, 2),
      size = _useState2[0],
      setSize = _useState2[1];

  var updateSize = useCallback(function () {
    var newSize = getWindowSize();

    if (currentSize.width !== newSize.width || currentSize.height !== newSize.height) {
      currentSize = newSize;
      setSize(newSize);
      return newSize;
    }

    return null;
  }, [setSize]);
  var onResize = useCallback(function () {
    var newSize = updateSize();

    if (newSize !== null && onChange !== null) {
      onChange(newSize);
    }
  }, [onChange]);
  useWindowEvent('resize', onResize);
  useEffect(function () {
    onResize();
  }, []);
  return size;
}

export { eventsManager$1 as documentEventsManager, getObserver, useCounter, useDailymotionPlayer, useDocumentEvent, useIntersectionObserver, useKeyboard, useNativeVideoPlayer, useObserver, usePlayerCurrentTime, useResizeObserver, useVideoPlayer, useVimeoPlayer, useWindowEvent, useWindowScroll, useWindowSize, useYouTubePlayer, eventsManager as windowEventsManager };
